/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Generic resizable side-panel column. Mirrors the drag logic that
// `ConversationListPanel` uses for the conversations list, but is
// reusable and supports a handle on EITHER edge so the right-docked
// utility panel (Threads / Notes / Tasks — Figma Ping-Desktop) can be
// dragged from its LEFT edge while a left-docked panel drags from its
// right edge.
//
// Web-only drag: the handle listens for `mousedown` then tracks
// `mousemove`/`mouseup` on `window`. On RN-macOS (no DOM `window`) the
// guards short-circuit and the panel renders at a fixed width.

import React, { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';

const HANDLE_HIT_WIDTH = 8;
const HANDLE_VISIBLE_WIDTH = 2;

export interface SidePanelFrameProps {
	readonly width: number;
	readonly minWidth: number;
	readonly maxWidth: number;
	readonly onResize: (next: number) => void;
	/** Which edge the drag handle lives on. */
	readonly handleSide: 'left' | 'right';
	readonly children: React.ReactNode;
	/** Extra classes for the panel column (background, borders). */
	readonly className?: string | undefined;
	readonly accessibilityLabel?: string | undefined;
}

export function SidePanelFrame({
	width,
	minWidth,
	maxWidth,
	onResize,
	handleSide,
	children,
	className,
	accessibilityLabel,
}: SidePanelFrameProps): JSX.Element {
	const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
	const [dragging, setDragging] = useState(false);

	useEffect(() => {
		if (
			typeof window === 'undefined' ||
			typeof (window as { addEventListener?: unknown }).addEventListener !==
				'function'
		) {
			return undefined;
		}
		const handleMove = (e: MouseEvent): void => {
			const drag = dragRef.current;
			if (drag === null) return;
			const delta = e.clientX - drag.startX;
			// A left-edge handle grows the panel when the cursor moves
			// LEFT (negative delta); a right-edge handle grows it moving
			// right.
			const signed = handleSide === 'left' ? -delta : delta;
			const next = Math.min(
				Math.max(drag.startWidth + signed, minWidth),
				maxWidth,
			);
			onResize(next);
		};
		const handleUp = (): void => {
			if (dragRef.current === null) return;
			dragRef.current = null;
			setDragging(false);
			document.body.style.cursor = '';
			document.body.style.userSelect = '';
		};
		window.addEventListener('mousemove', handleMove);
		window.addEventListener('mouseup', handleUp);
		return () => {
			window.removeEventListener('mousemove', handleMove);
			window.removeEventListener('mouseup', handleUp);
		};
	}, [handleSide, minWidth, maxWidth, onResize]);

	const beginDrag = (e: { clientX: number }): void => {
		dragRef.current = { startX: e.clientX, startWidth: width };
		setDragging(true);
		if (typeof document !== 'undefined') {
			document.body.style.cursor = 'col-resize';
			document.body.style.userSelect = 'none';
		}
	};

	return (
		<View
			accessibilityLabel={accessibilityLabel}
			className={`relative flex flex-col shrink-0 h-full min-h-0 ${className ?? ''}`}
			style={{ width }}
		>
			{children}
			<ResizeHandle
				side={handleSide}
				dragging={dragging}
				onBeginDrag={beginDrag}
			/>
		</View>
	);
}

interface ResizeHandleProps {
	readonly side: 'left' | 'right';
	readonly dragging: boolean;
	readonly onBeginDrag: (e: { clientX: number }) => void;
}

function ResizeHandle({
	side,
	dragging,
	onBeginDrag,
}: ResizeHandleProps): JSX.Element {
	const [hovered, setHovered] = useState(false);
	const showIndicator = hovered || dragging;

	const domProps = {
		onMouseDown: (e: { clientX: number; preventDefault?: () => void }) => {
			e.preventDefault?.();
			onBeginDrag(e);
		},
		onMouseEnter: () => setHovered(true),
		onMouseLeave: () => setHovered(false),
	} as unknown as Record<string, unknown>;

	return (
		<View
			accessibilityRole="adjustable"
			accessibilityLabel="Resize panel"
			className="absolute top-0 bottom-0 z-10"
			style={
				{
					cursor: 'col-resize',
					width: HANDLE_HIT_WIDTH,
					...(side === 'left'
						? { left: -(HANDLE_HIT_WIDTH / 2) }
						: { right: -(HANDLE_HIT_WIDTH / 2) }),
				} as unknown as object
			}
			{...domProps}
		>
			<View
				accessibilityElementsHidden
				className={`h-full mx-auto bg-light-accent-brand dark:bg-dark-accent-brand ${
					showIndicator ? 'opacity-70' : 'opacity-0'
				}`}
				style={{ width: HANDLE_VISIBLE_WIDTH, pointerEvents: 'none' }}
			/>
		</View>
	);
}
